import React from 'react';
import { BasicTable } from 'ui-lib';
import { ISelectableTableProps } from './record-picker.types';

export const DEFAULT_RECORD_KEY = 'uuid';

export const getRecordKey = <T,>(record: T, rowKey: string = DEFAULT_RECORD_KEY): string =>
    String((record as Record<string, unknown>)[rowKey]);

/** A paginated table whose rows can be selected with radios (single) or checkboxes (multiple). */
export const SelectableTable = <T,>(props: ISelectableTableProps<T>): JSX.Element => {
    const { source, columns, selectionType, rowKey = DEFAULT_RECORD_KEY, value = [], onChange } = props;

    const onSelectionChange = (selectedKeys: React.Key[], selectedRows: T[]) => {
        // Rows that are not loaded (e.g. saved values beyond the fetched pages) come back as undefined,
        // so fall back to the records already in the value
        const recordByKey = new Map<string, T>();
        [...value, ...selectedRows].filter(Boolean).forEach((record) => recordByKey.set(getRecordKey(record, rowKey), record));
        onChange?.(selectedKeys.map((key) => recordByKey.get(String(key))).filter((record): record is T => !!record));
    };

    return (
        <BasicTable
            rowKey={rowKey}
            loading={source.loading}
            dataSource={source.data}
            pagination={source.pagination}
            onTableChange={source.onTableChange}
            columns={columns}
            rowSelection={{
                type: selectionType === 'single' ? 'radio' : 'checkbox',
                selectedRowKeys: value.map((record) => getRecordKey(record, rowKey)),
                // Keep selected keys whose rows are not loaded yet
                preserveSelectedRowKeys: true,
                onChange: onSelectionChange,
            }}
        />
    );
};
